import React from 'react';
import Icon from '../Icon';
import '../../styles/components/StatusCard.css';

export type StatusCardPriority = 'high' | 'medium' | 'low' | 'neutral';

export interface StatusCardAction {
  icon: string;
  onClick: () => void;
  title?: string;
}

export interface StatusCardProps {
  /** Small uppercase eyebrow — the item's category/type. */
  category: string;
  priority?: StatusCardPriority;
  /** Text shown next to the category, right-aligned — defaults to the
   *  priority name uppercased when `priority` is set and this is omitted. */
  priorityLabel?: string;
  title: string;
  /** Stacked metadata lines, e.g. assignee, due date. */
  metaLines?: string[];
  actions?: StatusCardAction[];
  /** A flag (Overdue, Blocked) shown inside the card, above the category line. */
  badge?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const PRIORITY_COLOR: Record<StatusCardPriority, string> = {
  high: 'var(--danger, #ef4444)',
  medium: 'var(--warning, #f59e0b)',
  low: 'var(--text-faint, #9ca3af)',
  neutral: 'var(--border-strong, #d1d5db)',
};

/**
 * Priority-accented status card — for anything moving through a workflow
 * (a task, a stationery item) that reads as a scannable stack or kanban
 * column entry rather than a priced or contact-shaped record.
 */
const StatusCard: React.FC<StatusCardProps> = ({
  category,
  priority = 'neutral',
  priorityLabel,
  title,
  metaLines,
  actions,
  badge,
  onClick,
  className = '',
}) => (
  <div
    className={`sc-card ${className}`}
    style={{ /* ds-dynamic: priority color from map */ borderLeftColor: PRIORITY_COLOR[priority] }}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    {badge && <div className="sc-badge">{badge}</div>}
    <div className="sc-top">
      <span className="sc-category">{category}</span>
      <div className="sc-top-right">
        {priorityLabel !== undefined ? (
          priorityLabel && <span className={`sc-priority sc-priority-${priority}`}>{priorityLabel}</span>
        ) : (
          priority !== 'neutral' && <span className={`sc-priority sc-priority-${priority}`}>{priority.toUpperCase()}</span>
        )}
        {actions && actions.length > 0 && (
          <div className="sc-actions" onClick={e => e.stopPropagation()}>
            {actions.map((action, i) => (
              <button key={i} type="button" className="sc-action-btn" title={action.title} onClick={action.onClick}>
                <Icon name={action.icon} size={12} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
    <p className="sc-title">{title}</p>
    {metaLines && metaLines.length > 0 && (
      <div className="sc-meta">
        {metaLines.map((line, i) => <span key={i}>{line}</span>)}
      </div>
    )}
  </div>
);

export default StatusCard;
